import config from 'config';
import _ from 'lodash';
import { action, autorun, autorunAsync, computed, observable, reaction } from 'mobx';
// Import viewStore
import ViewStore from 'stores/ViewStore';
// Import API
import API from 'utils/api';
import Channel from './models/Channel';
import MediaContainer from './models/MediaContainer';
// Import Models
import Mediahub from './models/Mediahub';
import SpecialChannel from './models/SpecialChannel';


/**
 * MediahubStore Class
 */
class MediahubStore {

  // Basic Mediahub data
  @observable mediahub        = {};
  @observable channels        = [];
  @observable specialChannels = [];

  // Search results array
  @observable mediaContainers = [];

  // Channel handling
  @observable currentChannelId   = null;
  @observable currentContainerId = null;
  @observable currentContainer   = {};

  // Sorting
  @observable ascending  = false;
  @observable sortOption = _.head(config.sortOptions);

  // Searching
  @observable selectedFilters = observable.map({});
  @observable searchText      = '';
  @observable numberOfHits    = 0;

  // Loading
  @observable loadedInitialData   = false;
  @observable pendingRequestCount = 0;
  @observable nextBatchFrom       = 0;

  /**
   * CONSTRUCTOR
   */
  constructor() {

    // LOAD MEDIAHUB !!!!
    if(!config.includeAuthToken) {
      this.loadMediahub();
    } else {
      // If there has to be a token load mediahub
      // only after the API token is present
      reaction(
        () => API.token,
        () => {
          this.loadMediahub();
        },
      );
    }

    // SET CURRENT CHANNEL ID
    // Via pathname change
    autorun(() => {
      if (!ViewStore.currentView) return;
      const { name, channel, lang } = ViewStore.currentView;

      // Find channelId based on current view
      const channelId = _.result(_.find(this.channels, (c) => {
        return _.isEqual(channel, c.link)
               || _.isEqual(channel, c[`link_${lang}`]);
      }), 'id', false);

      if(channelId) {
        // Set current channel ID => triggers search
        this.setCurrentChannelId(channelId);
      }
    });

    // DELIVER DIRECTLINK
    // Only when conditions are met
    autorun(() => {
      if (!ViewStore.currentView) return;
      const { channel, detail } = ViewStore.currentView;

      if(detail && channel && (!config.includeAuthToken || !!API.token)) {
        // Search for directLink
        this.deliverDirectLink(`${channel}/${detail}`);
      }
    });

    // TRIGGER SEARCH
    // When search conditions change
    // Do it maximum once every 300ms
    autorunAsync(() => {
      // It's needed here for when to run
      // const filters     = this.selectedFilters.values();
      // const sortOption  = this.sortOption;
      // const ascending   = this.ascending;
      const searchText = this.searchText;
      const channelId  = this.currentChannelId;

      if(searchText || channelId) {
        this.queryBuilder();
      }

    }, 300);

    // LOAD NEXT BATCH OF RESULTS
    // For infinite wall component
    reaction(
      () => this.nextBatchFrom,
      () => {
        if(!!this.nextBatchFrom && this.nextBatchFrom < this.numberOfHits) {
          // console.warn("load next batch", this.nextBatchFrom);
          this.queryBuilder(this.nextBatchFrom);
        }
      },
    );


    // DEBUGING
    // autorun(() => {
    //   console.info("numberOfHits", this.numberOfHits);
    // });

  }

  // Is searching for something
  @computed
  get isSearching() {
    return !this.searchTextEmpty || !!this.selectedFilters.size;
  }

  @computed
  get searchTextEmpty() {
    return _.isEmpty(this.searchText);
  }

  @computed
  get isLoading() {
    return this.pendingRequestCount > 0;
  }

  @computed
  get currentChannel() {
    return this.channels.find((c) => c.id === this.currentChannelId) || false;
  }

  get featuredId() {
    return this.isDev
      ? config.featuredChannelIdDev
      : config.featuredChannelId;
  }

  get mostViewedId() {
    return this.isDev
      ? config.mostViewedChannelIdDev
      : config.mostViewedChannelId;
  }

  @computed
  get channelsWithoutFeatured() {
    const channels = this.channels.slice();
    const LANGUAGE_CHANNELS = {
      en: 'english',
      pl: 'polski',
      fr: 'francais',
    };
    const LANGUAGE_CHANNEL_LIST = Object.values(LANGUAGE_CHANNELS);

    if (_.startsWith(ViewStore.currentPath, '/de')) {
      return channels.filter((c) => c.id !== this.featuredId && !_.includes(LANGUAGE_CHANNEL_LIST, c.link));
    }

    if (_.startsWith(ViewStore.currentPath, '/pl')) {
      return channels.filter((c) => (c.link == LANGUAGE_CHANNELS.pl));
    }

    if (_.startsWith(ViewStore.currentPath, '/fr')) {
      return channels.filter((c) => (c.link == LANGUAGE_CHANNELS.fr));
    }

    return channels.filter((c) => (c.link == LANGUAGE_CHANNELS.en));
  }

  @computed
  get featuredChannel() {
    return this.channels.find((c) => c.id === this.featuredId) || {};
  }

  @computed
  get featuredMediaContainer() {
    const mediaContainers = _.sortBy(_.result(this.featuredChannel, 'teasers', []), 'sortCriteria');
    return _.head(mediaContainers);
  }

  get isDev() {
    return API.host().indexOf('apidev') > -1;
  }

  /**
   * GENERAL LOAD ERROR ACTION
   * Use in all failed responses
   */
  @action
  loadError(request, error) {
    this.pendingRequestCount--;
    console.warn(`loadError ${request}`, error);
  }


  /**
   * LOAD MEDIAHUB ACTIONS
   */
  @action
  loadMediahub() {
    this.pendingRequestCount++;

    API.get('init')
      .then((result) => this.loadMediahubSuccess(result))
      .fail((err) => this.loadError('loadMediahub', err));
  }

  @action
  loadMediahubSuccess(mediahub) {
    this.pendingRequestCount--;

    this.mediahub = new Mediahub(mediahub);
    this.loadChannels();
  }


  /**
   * LOAD CHANNELS ACTIONS
   */
  @action
  loadChannels() {
    this.pendingRequestCount++;

    API.get('mediahub')
      .then((result) => this.loadChannelsSuccess(result))
      .fail((err) => this.loadError('loadChannels', err));
  }

  @action
  loadChannelsSuccess(channels) {
    this.pendingRequestCount--;
    this.hasLoadedInitialData = true;

    //sort channels according to their position
    channels = _.orderBy(channels, 'sorting.pos', 'asc');

    // Add special channels
    const specials = ['publishedDate'];
    _.forEach(specials, (c) => {
      const channel = new SpecialChannel(c, false);
      this.specialChannels.push(channel);
    });

    _.forEach(channels, (c) => {
      let mostViewed = c.channelId === this.mostViewedId;
      const channel  = new Channel(c, mostViewed);
      // Most viewed is requested to be on the top of the list
      mostViewed
        ? this.channels.unshift(channel)
        : this.channels.push(channel);
    });
  }


  /**
   * DIRECT LINK ACTIONS
   */
  @action
  deliverDirectLink(directLink) {
    this.pendingRequestCount++;

    const data = {};

    data['directLink'] = directLink;
    if(config.includeAuthToken) {
      data['token'] = API.token;
    }

    API.get('directLink', data)
      .then((result) => this.deliverDirectLinkSuccess(result))
      .fail((err) => this.loadMediahubError(err));
  }

  @action
  deliverDirectLinkSuccess(result) {
    const mc = _.head(result.hits);

    if (!mc) {
        ViewStore.language === 'en' ? ViewStore.router.navigate('channel', { lang: 'en', channel:'english' }) : ViewStore.router.navigate('start') ;
        return;
    }

    this.setCurrentChannelId(mc.channelId);
    this.currentContainer = new MediaContainer(mc);
  }


  /**
   * CURRENT CHANNEL AND MEDIACONTAINER ACTIONS
   */
  @action
  setCurrentChannelId(channelId) {
    this.currentChannelId = channelId;
    this.clearSearch();
  }


  /**
   * SORTING ACTIONS
   */
  @action
  sorting(option, ascending) {
    this.sortOption = option || this.sortOption;
    this.ascending  = ascending;
  }

  @action
  initSorting() {
    this.sortOption = _.head(config.sortOptions);
    this.ascending  = false;
  }


  /**
   * LOAD NEXT BATCH ACTIONS
   */
  @action
  loadNextBatch() {
    this.nextBatchFrom = this.mediaContainers.length;
  }


  /**
   * SEARCH ACTIONS
   */
  @action
  setSearchText(searchText) {
    this.searchText = searchText;
  }

  @action
  setSearchFilter(filter, type) {
    const valueArray = _.join(filter, ',');

    if(!valueArray) {
      this.selectedFilters.delete(type);
    } else {
      this.selectedFilters.set(type, valueArray);
    }
  }

  @action
  clearSearch() {
    // Clear search inputs
    this.searchText = '';
    this.selectedFilters.clear();

    // Return to initial sorting
    this.ascending  = false;
    this.sortOption = _.head(config.sortOptions);
  }


  /**
   * SEARCH RESULTS ACTIONS
   */
  @action
  queryBuilder(nextBatchFrom) {
    // Prepare params object
    const params = {};

    // Prepare filters array
    const filter = [];

    const must = [];

    const nestedQuery = {
      "nested": {
        "path": "links",
        "query": {
          "bool": {
            "filter": [
              {
                "terms": {
                  "links.language": [ViewStore.language]
                }
              }
            ]
          }
        }
      }
    }


    must.push( nestedQuery )

    // Add selected filters to filters array
    this.selectedFilters.forEach((value, key) => {
      const terms = {};
      terms[key]  = _.compact(_.split(value, ','));

      if(key === 'orientation') {
        const orientationArr = [];

        _.forEach(terms[key], (mode) => {
          if(_.isEqual(mode, 'landscape')) {
            orientationArr.push(1, 2, 3, 4);
          }
          if(_.isEqual(mode, 'portrait')) {
            orientationArr.push(5, 6, 7, 8);
          }
        });

        terms[key] = orientationArr;
      }

      if(_.size(terms[key])) {
        filter.push({ terms });
      }
    });

    // Add search term if search text isn't empty
    if(!this.searchTextEmpty) {
      params['searchTerm'] = this.searchText;
    }

    // If there is no search term add sorting options
    if(this.searchTextEmpty) {
      const sortOrder            = {};
      sortOrder[this.sortOption] = {
        order: this.ascending
                 ? 'asc'
                 : 'desc',
      };
      params['sort']             = [sortOrder];
    }

    // If there are no filters yet or search text add channelId
    if(!_.size(filter) && !this.searchText && this.currentChannelId) {
      filter.push(
        { term: { channelId: this.currentChannelId } },
        // { term: { language: ViewStore.language } },
      );
    }

    // Add filters and nested query to the query
    if(_.size(filter)) {
      params['query'] = {
        bool: { filter, must },
      };
    }

    // Add from param in case of loading next batch
    if(nextBatchFrom) {
      params['from'] = nextBatchFrom;
    } else {
      // Empty out array
      this.mediaContainers = [];
    }

    // Add auth token
    if(config.includeAuthToken) {
      params['token'] = API.token;
    }

    // Perform the search
    this.search(params);
  }


  @action
  search(data) {
    this.pendingRequestCount++;

    API.post('search', { data })
      .then((result) => this.searchSuccess(result))
      .fail((err) => this.loadError('search 1', err));
  }


  // TODO -> abstract this whole block cause it's in 3 places
  // from searchSuccess till deliverThumbsSuccess
  @action
  searchSuccess(result) {
    this.pendingRequestCount--;
    let channelMediaContainerIds = [];

    this.numberOfHits = result.numberOfHits;

    if(_.isEqual(result.from, 0)) {
      this.mediaContainers = [];
    }


    if(this.mediaContainers.length <= this.numberOfHits) {
      // Add MediaContainers
      _.forEach(result.hits, (mc) => {
        const mediaContainer = new MediaContainer(mc);
        this.mediaContainers.push(mediaContainer);
        channelMediaContainerIds.push({
          mediaContainerId: mc.mediaContainerId,
          channelId:        mc.channelId,
        });

      });
    }

    // Deliver thumbnails for search results
    this.deliverThumbs({ channelMediaContainerIds });

  }

  @action
  deliverThumbs(data) {
    this.pendingRequestCount++;

    data.previewImages = true;
    data.width         = 480;

    API.post('deliverThumbs', { data })
      .then((result) => this.deliverThumbsSuccess(result))
      .fail((err) => this.loadError('deliverThumbs', err));
  }

  @action
  deliverThumbsSuccess(thumbs) {
    this.pendingRequestCount--;
    const thumbsByMc = _.groupBy(thumbs, 'mediaContainerId');

    _.forEach(thumbsByMc, (value, key) => {
      const mcs = _.filter(this.mediaContainers, { id: _.toInteger(key) });
      _.forEach(mcs, (mc) => {
        mc.setThumbnails(value);
      });
    });
  }
}

const mediahubStore = new MediahubStore();
export default mediahubStore;



// WEBPACK FOOTER //
// ./src/stores/MediahubStore.js